import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'ge_chat_session';

/**
 * Widget flotante de chat para visitantes de la landing.
 * Al pulsar el botón burbuja, abre un panel con formulario inicial
 * (nombre + email/tel opcional + mensaje). Después queda como chat
 * en vivo, hace polling cada 5 s a los mensajes de superadmin.
 */
export const ChatWidget = () => {
  const [abierto, setAbierto] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', mensaje: '' });
  const [enviando, setEnviando] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [cerrada, setCerrada] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  // Recuperar sesión previa: prioridad al parámetro ?chat=<sid> de la URL
  // (para enlaces enviados por email cuando el admin responde offline).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('chat');
    if (fromUrl && fromUrl.length > 10) {
      localStorage.setItem(STORAGE_KEY, fromUrl);
      setSessionId(fromUrl);
      setAbierto(true);
      // Limpiamos el param de la URL para no reabrir en refresh
      const url = new URL(window.location.href);
      url.searchParams.delete('chat');
      window.history.replaceState({}, '', url.toString());
      return;
    }
    const sid = localStorage.getItem(STORAGE_KEY);
    if (sid) setSessionId(sid);
  }, []);

  // Polling cuando hay sesión y widget abierto
  useEffect(() => {
    if (!sessionId || !abierto) return undefined;
    let cancelled = false;
    const cargar = async () => {
      try {
        const since = mensajes.length ? mensajes[mensajes.length - 1].timestamp : '';
        const r = await axios.get(`${API}/api/public/chat/${sessionId}/mensajes${since ? `?since=${encodeURIComponent(since)}` : ''}`);
        if (cancelled) return;
        if (r.data.estado === 'cerrada') setCerrada(true);
        if (r.data.mensajes.length > 0) {
          setMensajes((prev) => [...prev, ...r.data.mensajes]);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          // Sesión ya no existe, resetear
          localStorage.removeItem(STORAGE_KEY);
          setSessionId(null); setMensajes([]); setCerrada(false);
        }
      }
    };
    cargar(); // primera carga
    const t = setInterval(cargar, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [sessionId, abierto, mensajes]);

  // Auto-scroll al fondo
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensajes, abierto]);

  const iniciar = async (e) => {
    e.preventDefault();
    setError('');
    if (form.nombre.trim().length < 2) { setError('Escribe tu nombre'); return; }
    if (form.mensaje.trim().length < 2) { setError('Escribe tu mensaje'); return; }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/api/public/chat/iniciar`, form);
      const sid = r.data.session_id;
      localStorage.setItem(STORAGE_KEY, sid);
      setSessionId(sid);
      setMensajes([{ id: 'init', sender: 'visitor', contenido: form.mensaje.trim(), timestamp: new Date().toISOString() }]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido iniciar el chat.');
    } finally { setEnviando(false); }
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (nuevoMensaje.trim().length === 0 || cerrada) return;
    const contenido = nuevoMensaje.trim();
    setNuevoMensaje('');
    setMensajes((p) => [...p, { id: `local-${Date.now()}`, sender: 'visitor', contenido, timestamp: new Date().toISOString() }]);
    try {
      await axios.post(`${API}/api/public/chat/${sessionId}/mensaje`, { contenido });
    } catch (err) {
      setError('No se pudo enviar el mensaje.');
    }
  };

  const reiniciar = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSessionId(null); setMensajes([]); setCerrada(false); setError('');
    setForm({ nombre: '', email: '', telefono: '', mensaje: '' });
  };

  return (
    <>
      {/* Burbuja flotante */}
      {!abierto && (
        <button
          onClick={() => setAbierto(true)}
          data-testid="chat-widget-open"
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-3 rounded-full bg-terracotta text-white shadow-2xl hover:bg-terracotta-hover transition-all duration-300 pl-5 pr-6 py-4 font-body-landing font-bold"
          aria-label="Abrir chat"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline text-sm">¿Alguna duda? Chatea con nosotros</span>
          <span className="sm:hidden text-sm">Chatea</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-forest-100 ring-4 ring-terracotta animate-pulse" />
        </button>
      )}

      {/* Panel de chat */}
      {abierto && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[92vw] sm:w-[380px] max-w-sm h-[560px] rounded-2xl bg-white shadow-2xl border border-forest-900/10 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          data-testid="chat-widget-panel"
        >
          <div className="bg-forest-900 text-bone p-4 flex items-center justify-between shrink-0">
            <div>
              <p className="font-display font-bold text-base">Chat con GestionaEmpleados</p>
              <p className="font-body-landing text-xs text-bone/60">
                {sessionId ? (cerrada ? 'Sesión cerrada' : 'Te responderemos en unos minutos') : 'Habla con nuestro equipo comercial'}
              </p>
            </div>
            <button onClick={() => setAbierto(false)} data-testid="chat-widget-close"
              className="w-8 h-8 rounded-full hover:bg-bone/10 flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>

          {!sessionId ? (
            /* Formulario inicial */
            <form onSubmit={iniciar} className="flex-1 overflow-y-auto p-5 space-y-3 font-body-landing bg-bone">
              <p className="text-sm text-forest-700 leading-relaxed">
                Cuéntanos en qué podemos ayudarte y te contestamos en directo. Si prefieres, llámanos al{' '}
                <a href="tel:+34680292427" className="font-bold text-forest-900 underline">680 292 427</a>.
              </p>
              <div>
                <label className="text-xs font-bold text-forest-900">Nombre *</label>
                <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-forest-900/15 bg-white text-sm"
                  placeholder="Tu nombre" data-testid="chat-init-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-forest-900">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-forest-900/15 bg-white text-sm"
                    placeholder="opcional" data-testid="chat-init-email" />
                </div>
                <div>
                  <label className="text-xs font-bold text-forest-900">Teléfono</label>
                  <input type="tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-forest-900/15 bg-white text-sm"
                    placeholder="opcional" data-testid="chat-init-telefono" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-forest-900">Tu mensaje *</label>
                <textarea required rows={4} value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-forest-900/15 bg-white text-sm resize-none"
                  placeholder="¿En qué podemos ayudarte?" data-testid="chat-init-mensaje" />
              </div>
              {error && <p className="text-xs text-terracotta font-bold">{error}</p>}
              <button type="submit" disabled={enviando}
                className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-bold h-11 flex items-center justify-center gap-2 text-sm"
                data-testid="chat-init-submit">
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Iniciar chat <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-[10px] text-forest-700/50 text-center">
                Sin compromiso. Datos solo usados para responderte.
              </p>
            </form>
          ) : (
            <>
              {/* Historial de mensajes */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-forest-100/30 font-body-landing" data-testid="chat-mensajes-scroll">
                {mensajes.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}
                    data-testid={`chat-msg-${m.sender}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                      m.sender === 'visitor'
                        ? 'bg-terracotta text-white rounded-br-sm'
                        : 'bg-white text-forest-900 border border-forest-900/10 rounded-bl-sm'
                    }`}>
                      {m.contenido}
                    </div>
                  </div>
                ))}
                {cerrada && (
                  <div className="text-center pt-4">
                    <p className="text-xs text-forest-700/60 mb-2">Este chat se ha cerrado</p>
                    <button onClick={reiniciar} className="text-xs text-terracotta font-bold underline" data-testid="chat-reiniciar">
                      Iniciar un nuevo chat
                    </button>
                  </div>
                )}
              </div>

              {/* Input de mensaje */}
              {!cerrada && (
                <form onSubmit={enviar} className="p-3 border-t border-forest-900/10 flex items-center gap-2 bg-white shrink-0">
                  <input value={nuevoMensaje} onChange={(e) => setNuevoMensaje(e.target.value)}
                    placeholder="Escribe tu mensaje..."
                    className="flex-1 px-3 py-2 rounded-full border border-forest-900/15 bg-forest-100/40 text-sm font-body-landing"
                    data-testid="chat-input" />
                  <button type="submit" disabled={!nuevoMensaje.trim()}
                    className="w-9 h-9 rounded-full bg-terracotta text-white flex items-center justify-center hover:bg-terracotta-hover disabled:opacity-40 shrink-0"
                    data-testid="chat-send">
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};
